import React, { useState } from 'react';
import styled from 'styled-components';
import { Control, Controller } from 'react-hook-form';

import { TextFieldName } from '@/features/course/admin/constants/settingsModal';
import { Course } from '@/features/course/admin/types/course';
import { ButtonGroup } from '@/ui/ButtonGroup';
import {
  WrapStyled,
  ColumnStyled,
  StyledLabel,
  StyledCaption,
} from '@/features/course/admin/molecules/CourseSettingsModalForm/CourseSettingsModalForm';
import { useLanguages } from '@/store/languages';
import { Command, CommandEmpty, CommandInput, CommandItem, CommandList } from '@/ui/Command';
import { Popover, PopoverContent, PopoverTrigger } from '@/ui/Popover/Popover';
import { ComboboxTrigger } from '@/ui/Combobox';

type CourseSettingsModalFieldLanguageProps = {
  control: Control<Course>;
};

const PopoverContentStyled = styled(PopoverContent)`
  width: 336px;
`;

const ComboboxTriggerBody = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;

  min-height: 32px;
`;

export const CourseSettingsModalFieldLanguage = ({ control }: CourseSettingsModalFieldLanguageProps): JSX.Element => {
  const { languages } = useLanguages();
  const [popoverOpen, setPopoverOpen] = useState(false);

  return (
    <WrapStyled>
      <ColumnStyled>
        <ButtonGroup gap="small" direction="vertical">
          <StyledLabel>Language</StyledLabel>
          <StyledCaption>Choose the main language of this course</StyledCaption>
        </ButtonGroup>
      </ColumnStyled>
      <ColumnStyled>
        <Controller
          control={control}
          name={TextFieldName.Language}
          render={({ field: { onChange, value } }) => {
            const language = languages.find((l) => l.id === value);
            const name = language?.name || '';

            return (
              <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
                <PopoverTrigger asChild>
                  <ComboboxTrigger
                    comboboxOpen={popoverOpen}
                    label="Language"
                    onClick={() => {
                      setPopoverOpen(true);
                    }}
                  >
                    <ComboboxTriggerBody>{name}</ComboboxTriggerBody>
                  </ComboboxTrigger>
                </PopoverTrigger>
                <PopoverContentStyled align="start">
                  <Command>
                    <CommandInput placeholder="Search languages" />
                    <CommandList>
                      <CommandEmpty>No results found.</CommandEmpty>
                      {languages.map((language) => (
                        <CommandItem
                          key={language.id}
                          keywords={language.name?.split(' ')}
                          value={String(language.id)}
                          onSelect={(value) => {
                            onChange(Number(value));
                            setPopoverOpen(false);
                          }}
                          $selected={language.id === value}
                        >
                          {language.name}
                        </CommandItem>
                      ))}
                    </CommandList>
                  </Command>
                </PopoverContentStyled>
              </Popover>
            );
          }}
        />
      </ColumnStyled>
    </WrapStyled>
  );
};
